import React, { useMemo } from "react";
import { connect } from "react-redux";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import cn from "classnames";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Avatar, AvatarWithName } from "@mapmycustomers/ui";

import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import SharingModal from "./components/SharingModal";
import styles from "./SharePicker.module.scss";

interface Props {
  creatorUserId: User["id"];
  editable?: boolean;
  isGroupSharing?: boolean;
  loading?: boolean;
  onChange: (userIds: User["id"][]) => void;
  userIds: User["id"][];
  users: User[];
}

const SharePicker: React.FC<Props> = ({
  creatorUserId,
  editable = true,
  isGroupSharing,
  loading,
  onChange,
  userIds,
  users,
}) => {
  const intl = useIntl();

  const sharedUsers = useMemo(() => {
    const userById = new Map(users.map((user) => [user.id, user]));
    // put creator to the first position
    return [creatorUserId, ...userIds.filter((id) => id !== creatorUserId)]
      .map((userId) => userById.get(userId))
      .filter(isDefined);
  }, [creatorUserId, userIds, users]);

  const [sharingModalVisible, showSharingModal, hideSharingModal] = useBoolean();

  return (
    <>
      <div
        className={cn(styles.container, { [styles.editable]: editable })}
        onClick={showSharingModal}
      >
        <div className={styles.list}>
          <span className={styles.caption}>
            {intl.formatMessage({
              id: "map.sharePicker",
              defaultMessage: "Share with...",
              description: "Share picker caption",
            })}
            &nbsp;
          </span>
          {sharedUsers.slice(0, 3).map((user) => (
            <Avatar className={styles.item} key={user.id} user={user} />
          ))}
          {sharedUsers.length > 3 && (
            <Popover
              content={
                <div className={styles.overflownUserList}>
                  {sharedUsers.slice(3).map((user) => (
                    <AvatarWithName avatarClassName={styles.avatar} key={user.id} user={user} />
                  ))}
                </div>
              }
            >
              <div className={styles.tripleDotAvatar}>
                <FontAwesomeIcon icon={faEllipsisH} />
              </div>
            </Popover>
          )}
        </div>
      </div>
      {sharingModalVisible && (
        <SharingModal
          creatorUserId={creatorUserId}
          isGroupSharing={isGroupSharing}
          loading={loading}
          onChange={onChange}
          onHide={hideSharingModal}
          userIds={userIds}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

export default connect(mapStateToProps)(SharePicker);
